import api from '@anm/api';
import asyncEntity from '@anm/helpers/redux/asyncEntity';
import { call, put, select, take } from '@anm/helpers/saga/effects';
import { getType } from '@anm/helpers/saga/typesafe-actions';
import { appNotificationActions } from '@anm/store/modules/appNotifications';

import configs from '../../../config';

import { domainsActions } from '.';
import { selectDomainsList, selectNewDomain } from './selectors';

const { urls } = configs();

const fetchDomainsAsync = asyncEntity(domainsActions.fetchDomainsListAsync, () =>
  api().domains.getUserDomains(urls.customDomainUrl)
);
const addNewDomainAsync = asyncEntity(domainsActions.addNewDomainAsync, domain =>
  api().domains.generateCert({ host: domain, baseURL: urls.customDomainUrl })
);
const removeDomainAsync = asyncEntity(domainsActions.removeDomainAsync, domain =>
  api().domains.removeCert({ host: domain, baseURL: urls.customDomainUrl })
);

function* watchFetchDomainsList() {
  while (true) {
    yield take(getType(domainsActions.fetchDomainsList));

    yield call(fetchDomainsAsync);
  }
}

function* watchAddNewDomain() {
  while (true) {
    const { payload: domain }: ReturnType<typeof domainsActions.addNewDomain> = yield take(
      getType(domainsActions.addNewDomain)
    );

    yield call(addNewDomainAsync, domain);
  }
}

function* watchRemoveDomain() {
  while (true) {
    const { payload: domain }: ReturnType<typeof domainsActions.removeDomain> = yield take(
      getType(domainsActions.removeDomain)
    );

    yield call(removeDomainAsync, domain);
  }
}

function* watchAddNewDomainSuccess() {
  while (true) {
    yield take(getType(domainsActions.addNewDomainAsync.success));

    yield* put(
      appNotificationActions.notify({
        type: 'success',
        description: 'Domain added successfully'
      })
    );
  }
}

function* watchAddNewDomainFailure() {
  while (true) {
    yield take(getType(domainsActions.addNewDomainAsync.failure));

    const newDomain = yield* select(selectNewDomain);
    const addedDomains = yield* select(selectDomainsList);
    const isAlreadyAdded = !!addedDomains?.find(d => d.Host === newDomain);

    if (isAlreadyAdded) {
      yield* put(
        appNotificationActions.notify({
          type: 'error',
          description: `Domain <b>${newDomain}</b> is already added`
        })
      );
    } else {
      yield* put(
        appNotificationActions.notify({
          type: 'error',
          description: 'Domain was not added, please check CNAME settings or try again later'
        })
      );
    }
  }
}

function* watchRemoveDomainSuccess() {
  while (true) {
    yield take(getType(domainsActions.removeDomainAsync.success));

    yield* put(
      appNotificationActions.notify({
        type: 'success',
        description: 'Domain was deleted successfully'
      })
    );
  }
}

function* watchRemoveDomainFailure() {
  while (true) {
    yield take(getType(domainsActions.removeDomainAsync.failure));

    yield* put(
      appNotificationActions.notify({
        type: 'error',
        description: 'Domain was not deleted, please try again later'
      })
    );
  }
}

export default () => [
  call(watchFetchDomainsList),
  call(watchAddNewDomain),
  call(watchRemoveDomain),
  call(watchAddNewDomainSuccess),
  call(watchAddNewDomainFailure),
  call(watchRemoveDomainSuccess),
  call(watchRemoveDomainFailure)
];
